import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpContextToken } from '@angular/common/http';
import { isDate, format } from 'date-fns';

/**
 * Сұраныс денесіндегі күндерді түрлендірмеу.
 * Бэкенд уақыт белдеуі бар күн күтсе (OffsetDateTime/Instant), interceptor
 * `+05:00`/`Z`-ті кесіп тастамауы үшін қолданылады.
 */
export const SKIP_DATE_CONVERSION = new HttpContextToken<boolean>(() => false);

@Injectable()
export class DateInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (req.context.get(SKIP_DATE_CONVERSION)) {
      return next.handle(req);
    }

    let body = req.body;

    if (body instanceof FormData) {
      body = this.convertFormData(body);
    } else if (body instanceof URLSearchParams) {
      body = this.convertURLSearchParams(body);
    } else if (body instanceof Blob) {
      // Blob данные не трогаем, они бинарные
    } else if (typeof body === 'string') {
      // Если строка похожа на JSON, пробуем конвертировать даты внутри
      try {
        const parsed = JSON.parse(body);
        const converted = this.convertDates(parsed);
        body = JSON.stringify(converted);
      } catch {
        // Если не JSON, оставляем как есть
      }
    } else {
      body = this.convertDates(body);
    }

    const clonedReq = req.clone({ body });
    return next.handle(clonedReq);
  }

  private convertURLSearchParams(params: URLSearchParams): URLSearchParams {
    const newParams = new URLSearchParams();

    params.forEach((value, key) => {
      // Проверяем, похоже ли значение на дату
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        newParams.append(key, value + 'T00:00:00');
      } else {
        newParams.append(key, value);
      }
    });

    return newParams;
  }

  private convertFormData(formData: FormData): FormData {
    const newFormData = new FormData();

    formData.forEach((value: any, key) => {
      if (value instanceof Date) {
        newFormData.append(key, format(value, "yyyy-MM-dd'T00:00:00'"));
      } else if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        newFormData.append(key, value + 'T00:00:00');
      } else if (value instanceof File || value instanceof Blob) {
        newFormData.append(key, value);
      } else {
        // If the value is an object or array, try to convert dates inside it
        try {
          const parsed = JSON.parse(value as string);
          const converted = this.convertDates(parsed);
          newFormData.append(key, JSON.stringify(converted));
        } catch {
          // If parsing fails, append the original value
          newFormData.append(key, value);
        }
      }
    });

    return newFormData;
  }

  private convertDates(data: any): any {
    // Обработка строковых дат
    if (typeof data === 'string') {
      const dateStr = String(data).trim();

      // Проверяем ISO дату с timezone
      if (dateStr.includes('T') && (dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr))) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return format(date, "yyyy-MM-dd'T'HH:mm:ss");
        }
      }

      // Проверяем простую дату YYYY-MM-DD
      // Если дата имеет формат YYYY-MM-DD и содержит специальный маркер '_notime',
      // то оставляем её как есть, без добавления времени
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}_notime$/)) {
        return dateStr.replace('_notime', '');
      }
      // Иначе, обрабатываем обычную дату YYYY-MM-DD
      else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return format(new Date(dateStr), "yyyy-MM-dd'T'HH:mm:ss");
      }

      return data;
    }

    if (!data || typeof data !== 'object') return data;

    // Обработка объекта Date
    if (isDate(data)) {
      return format(data, "yyyy-MM-dd'T'HH:mm:ss");
    }

    // Рекурсивная обработка массивов
    if (Array.isArray(data)) {
      return data.map((item) => this.convertDates(item));
    }

    // Рекурсивная обработка объектов
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, this.convertDates(value)]));
  }
}
